import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../api';
import { ActiveUserRole, UserType } from '../types';
import { ClientUserProfile, PartnerUserProfile, SuperUser } from '../api/users.types';
import { isLogged } from '../utils';

interface UserContextInterface {
  data?: ClientUserProfile | SuperUser | PartnerUserProfile;
  isLoading: boolean;
  role: {
    role?: UserType;
  } & ActiveUserRole;
  isAdminOrganization: boolean;
  setRole: Dispatch<SetStateAction<UserType | undefined>>;
}

export const UserContext = createContext<UserContextInterface>({
  role: {
    isAdmin: false,
    isPartner: false,
    isClient: false
  },
  setRole: () => null,
  isAdminOrganization: false,
  isLoading: false
});

export const UserProvider = ({ children }: PropsWithChildren) => {
  const [role, setRole] = useState<UserType>();
  // Re-render on route change so after login/logout + navigate we re-read isLogged() and show correct user
  useLocation();
  const loggedIn = isLogged();

  // First get basic user data to determine role
  const { data: basicUserData, isLoading: isLoadingBasic } = useQuery({
    ...usersApi.getLoggedUserData(),
    onSuccess: (data) => setRole(data.role),
    enabled: loggedIn
  });

  useEffect(() => {
    if (basicUserData?.role && role !== basicUserData.role) {
      setRole(basicUserData.role);
    }
  }, [basicUserData, role]);

  // Then get detailed data based on role
  const { data: partnerData, isLoading: isLoadingPartner } = useQuery({
    ...usersApi.getPartnerDetails(),
    enabled: loggedIn && role === UserType.partner
  });

  const { data: clientData, isLoading: isLoadingClient } = useQuery({
    ...usersApi.getClientDetails(),
    enabled: loggedIn && role === UserType.client
  });

  // Use detailed data if available, otherwise fall back to basic data
  const data =
    role === UserType.partner
      ? partnerData ?? basicUserData
      : role === UserType.client
        ? clientData ?? basicUserData
        : basicUserData;

  // Consider loading true while the role/data queries are in flight
  const isLoading =
    loggedIn &&
    (isLoadingBasic ||
      (role === UserType.partner && isLoadingPartner) ||
      (role === UserType.client && isLoadingClient));

  if (process.env.NODE_ENV !== 'production') {
    // Temporary debug to trace infinite loading in dev.
    // eslint-disable-next-line no-console
    console.log('[UserContext] loading state', {
      isLoadingBasic,
      isLoadingPartner,
      isLoadingClient,
      role,
      hasBasic: Boolean(basicUserData),
      hasPartner: Boolean(partnerData),
      hasClient: Boolean(clientData),
      isLogged: loggedIn
    });
  }

  const isAdminOrganization = !!(
    [UserType.admin, UserType.partner].includes(role!) || data?.is_organization_administrator
  );

  return (
    <UserContext.Provider
      value={{
        data,
        isLoading,
        role: {
          role,
          isAdmin: role === UserType.admin,
          isClient: role === UserType.client,
          isPartner: role === UserType.partner
        },
        isAdminOrganization,
        setRole
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
